import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useCopilot } from "@/context/CopilotContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sparkles, X, Bot, User, Send, Globe, CheckCircle2, Edit3, MinusCircle,
  Loader2, AlertCircle, GripVertical, RotateCcw, SkipForward, HelpCircle,
  Workflow, GitBranch, Zap, PartyPopper,
} from "lucide-react";

const MIN_WIDTH = 340;
const MAX_WIDTH = 640;
const DEFAULT_WIDTH = 440;
const WIDTH_STORAGE_KEY = "peak:copilot_width";

function loadStoredWidth() {
  try {
    const raw = localStorage.getItem(WIDTH_STORAGE_KEY);
    const n = raw ? parseInt(raw, 10) : NaN;
    if (Number.isFinite(n)) return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, n));
  } catch { /* ignore */ }
  return DEFAULT_WIDTH;
}

function renderMarkdownLite(text) {
  if (!text) return null;
  // Split into paragraphs on blank lines, bold **x**, keep it simple.
  const paragraphs = String(text).split(/\n\n+/);
  return paragraphs.map((p, i) => {
    const parts = p.split(/(\*\*[^*]+\*\*)/g);
    return (
      <p key={i} className="whitespace-pre-wrap leading-relaxed">
        {parts.map((chunk, j) => {
          if (/^\*\*[^*]+\*\*$/.test(chunk)) {
            return <strong key={j}>{chunk.slice(2, -2)}</strong>;
          }
          return <span key={j}>{chunk}</span>;
        })}
      </p>
    );
  });
}

export default function CopilotDrawer() {
  const {
    open, demo, session, busy, error,
    closeCopilot, sendMessage, submitWebsite, verify, dismiss,
    stopDemo, resetOnboarding, isSuperadmin,
    approveRec, skipRec, explainRec, editRec, finishWalkthrough,
  } = useCopilot();
  const [input, setInput] = useState("");
  const [urlDraft, setUrlDraft] = useState("");
  const [width, setWidth] = useState(loadStoredWidth);
  const [dragging, setDragging] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Drag-to-resize handlers — drawer sits on the right, so dragging the
  // handle LEFT should INCREASE width. We track deltaX from the last event.
  const onDragStart = useCallback((e) => {
    e.preventDefault();
    setDragging(true);
    const startX = e.clientX;
    const startWidth = width;
    const move = (ev) => {
      const delta = startX - ev.clientX; // drag left = positive delta
      const next = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth + delta));
      setWidth(next);
    };
    const up = () => {
      setDragging(false);
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      try { localStorage.setItem(WIDTH_STORAGE_KEY, String(width)); } catch { /* ignore */ }
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  }, [width]);

  // Persist width whenever it settles (small effect after drag ends)
  useEffect(() => {
    if (!dragging) {
      try { localStorage.setItem(WIDTH_STORAGE_KEY, String(width)); } catch { /* ignore */ }
    }
  }, [width, dragging]);

  const messages = session?.messages || [];
  const state = session?.state;
  const profile = session?.business_profile;
  const recommendations = session?.recommendations || [];
  const walkthroughIndex = session?.walkthrough_index ?? 0;
  const location = useLocation();
  const onRecsPage = location.pathname.startsWith("/copilot/recommendations");
  const showWebsiteInput = state === "greeting";
  const showVerifyPanel = state === "verifying_business" && !!profile;
  // When the dedicated Recommendations page is open, hide the in-drawer
  // walkthrough card — the chat drawer becomes purely conversational alongside
  // the page. Otherwise (dashboard, contacts, etc.) show the guided walkthrough.
  const showWalkthrough = !onRecsPage && (state === "walkthrough" || state === "completed") && recommendations.length > 0;
  const showChat = !showWebsiteInput || (messages?.length || 0) > 1;

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, busy]);

  // Focus input on open
  useEffect(() => {
    if (open && inputRef.current && !showWebsiteInput) {
      const t = setTimeout(() => inputRef.current?.focus(), 200);
      return () => clearTimeout(t);
    }
  }, [open, showWebsiteInput]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const msg = input.trim();
    if (!msg) return;
    setInput("");
    await sendMessage(msg);
  };

  const handleUrl = async (e) => {
    e?.preventDefault();
    const url = urlDraft.trim();
    if (!url) return;
    setUrlDraft("");
    await submitWebsite(url);
  };

  return (
    <div
      className={`sticky top-0 h-screen shrink-0 bg-white border-l-2 border-[var(--peak-ink)] shadow-[-8px_0_32px_-16px_rgba(0,0,0,0.25)] flex flex-col z-30 relative ${dragging ? "select-none" : ""}`}
      style={{ width: `${width}px` }}
      data-testid="copilot-drawer"
      data-demo={demo ? "1" : "0"}
    >
      {/* Drag handle: sits on the LEFT edge of the drawer */}
      <div
        onMouseDown={onDragStart}
        data-testid="copilot-resize-handle"
        title="Drag to resize"
        className={`group absolute top-0 -left-1 w-2 h-full cursor-col-resize z-40 flex items-center justify-center ${dragging ? "" : "hover:bg-[var(--peak-accent)]/10"}`}
      >
        <div className={`w-0.5 h-8 bg-[var(--peak-ink)]/20 group-hover:bg-[var(--peak-accent)] transition-colors ${dragging ? "bg-[var(--peak-accent)]" : ""}`} />
        <GripVertical className={`absolute w-3 h-3 text-[var(--peak-ink)]/40 group-hover:text-[var(--peak-accent)] transition-opacity ${dragging ? "text-[var(--peak-accent)] opacity-100" : "opacity-0 group-hover:opacity-100"}`} />
      </div>
      {/* Header */}
      <div className="border-b-2 border-[var(--peak-ink)] bg-[var(--peak-ink)] text-white px-4 py-3 flex items-center gap-3">
        <div className="w-9 h-9 bg-[var(--peak-accent)] flex items-center justify-center flex-none">
          <Bot className="w-5 h-5" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-black tracking-tighter text-base leading-none flex items-center gap-2">
            PEAK COPILOT
            {demo && (
              <span
                className="text-[9px] font-mono uppercase tracking-widest bg-[var(--peak-accent)] text-white px-1.5 py-0.5 border border-[var(--peak-accent)]"
                data-testid="copilot-demo-badge"
              >
                DEMO
              </span>
            )}
          </div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] opacity-60 mt-1">
            {state ? state.replace(/_/g, " ") : "loading…"}
          </div>
        </div>
        {isSuperadmin && (
          <button
            onClick={async () => {
              if (!window.confirm(demo ? "Reset demo copilot session?" : "Reset your copilot session? This clears the current onboarding conversation.")) return;
              await resetOnboarding();
            }}
            data-testid="copilot-reset"
            title={demo ? "Reset demo session" : "Reset copilot session (superadmin)"}
            className="p-1.5 hover:bg-white/10"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        )}
        {demo && (
          <button
            onClick={stopDemo}
            data-testid="copilot-stop-demo"
            title="Exit demo mode"
            className="p-1.5 hover:bg-white/10"
          >
            <MinusCircle className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={closeCopilot}
          data-testid="copilot-close"
          title="Close"
          className="p-1.5 hover:bg-white/10"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {/* Messages */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto p-4 space-y-3"
          data-testid="copilot-messages"
        >
          {(!session && busy) && (
            <div className="flex items-center gap-2 text-sm text-[var(--peak-muted)]">
              <Loader2 className="w-4 h-4 animate-spin" /> Waking up…
            </div>
          )}
          {messages.map((m, i) => (
            <Message key={i} role={m.role} content={m.content} />
          ))}
          {busy && messages.length > 0 && (
            <div className="flex items-center gap-2 text-xs text-[var(--peak-muted)] pl-11" data-testid="copilot-typing">
              <span className="inline-flex gap-1">
                <span className="w-1.5 h-1.5 bg-[var(--peak-ink)] animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 bg-[var(--peak-ink)] animate-bounce" style={{ animationDelay: "120ms" }} />
                <span className="w-1.5 h-1.5 bg-[var(--peak-ink)] animate-bounce" style={{ animationDelay: "240ms" }} />
              </span>
              thinking…
            </div>
          )}
          {error && (
            <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border-2 border-red-200 p-3">
              <AlertCircle className="w-4 h-4 flex-none mt-0.5" />
              <span data-testid="copilot-error">{String(error)}</span>
            </div>
          )}
        </div>

        {/* Verify panel */}
        {showVerifyPanel && <VerifyPanel profile={profile} onVerify={verify} busy={busy} />}

        {/* Quick-nav to the full plan page when we're not already there */}
        {onRecsPage === false && (state === "walkthrough" || state === "completed") && recommendations.length > 0 && (
          <div className="border-t-2 border-[var(--peak-ink)] bg-[var(--peak-bg)] p-3">
            <a
              href={demo ? "/copilot/recommendations?demo=1" : "/copilot/recommendations"}
              data-testid="copilot-open-plan-page"
              className="flex items-center justify-between px-3 py-2 bg-[var(--peak-ink)] text-white text-[11px] font-mono uppercase tracking-[0.2em] hover:bg-[var(--peak-accent)] transition-colors"
            >
              <span>Open the full plan →</span>
              <span className="text-[10px] opacity-80">{recommendations.length} recs</span>
            </a>
          </div>
        )}

        {/* Walkthrough panel — Wave 2 (drawer-only mode) */}
        {showWalkthrough && (
          <WalkthroughPanel
            recommendations={recommendations}
            walkthroughIndex={walkthroughIndex}
            state={state}
            busy={busy}
            onApprove={approveRec}
            onSkip={skipRec}
            onExplain={explainRec}
            onEdit={editRec}
            onFinish={finishWalkthrough}
          />
        )}

        {/* Composer */}
        <div className="border-t-2 border-[var(--peak-ink)] bg-[var(--peak-bg)] p-3">
          {showWebsiteInput ? (
            <form onSubmit={handleUrl} className="flex flex-col gap-2" data-testid="copilot-url-form">
              <label className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--peak-muted)] flex items-center gap-1.5">
                <Globe className="w-3 h-3" /> Your business website
              </label>
              <div className="flex gap-2">
                <Input
                  value={urlDraft}
                  onChange={(e) => setUrlDraft(e.target.value)}
                  placeholder="acme.com"
                  data-testid="copilot-url-input"
                  className="h-10 border-2 border-[var(--peak-ink)] font-mono text-xs"
                  disabled={busy}
                  autoFocus
                />
                <Button
                  type="submit"
                  disabled={busy || !urlDraft.trim()}
                  data-testid="copilot-url-submit"
                  className="h-10 px-4 bg-[var(--peak-accent)] hover:bg-[var(--peak-ink)] text-white font-heading font-bold uppercase text-xs tracking-wide"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Analyze →"}
                </Button>
              </div>
              <button
                type="button"
                onClick={() => sendMessage("I don't have a website — let's do it the guided way.")}
                data-testid="copilot-skip-website"
                className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] hover:text-[var(--peak-ink)] self-start"
              >
                → I don&apos;t have a website
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="flex gap-2" data-testid="copilot-chat-form">
              <Textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                placeholder="Reply to the copilot…"
                rows={2}
                data-testid="copilot-input"
                className="min-h-[42px] border-2 border-[var(--peak-ink)] text-sm resize-none"
                disabled={busy}
              />
              <Button
                type="submit"
                disabled={busy || !input.trim()}
                data-testid="copilot-send"
                title="Send (Enter)"
                className="h-auto px-3 bg-[var(--peak-accent)] hover:bg-[var(--peak-ink)] text-white"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </form>
          )}
          {state !== "completed" && state !== "ready_for_wave2" && !demo && (
            <button
              onClick={dismiss}
              data-testid="copilot-dismiss"
              className="mt-2 text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] hover:text-[var(--peak-ink)]"
            >
              Dismiss for now
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Message({ role, content }) {
  const isUser = role === "user";
  return (
    <div
      className={`flex gap-2 ${isUser ? "flex-row-reverse" : ""}`}
      data-testid={`copilot-message-${role}`}
    >
      <div
        className={`w-8 h-8 flex-none flex items-center justify-center border-2 border-[var(--peak-ink)] ${
          isUser ? "bg-white" : "bg-[var(--peak-accent)] text-white"
        }`}
      >
        {isUser ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
      </div>
      <div
        className={`max-w-[85%] text-sm px-3 py-2 border-2 border-[var(--peak-ink)] ${
          isUser ? "bg-[var(--peak-ink)] text-white" : "bg-white text-[var(--peak-ink)]"
        }`}
      >
        {renderMarkdownLite(content)}
      </div>
    </div>
  );
}

function VerifyPanel({ profile, onVerify, busy }) {
  const [editing, setEditing] = useState(false);
  const [edits, setEdits] = useState(() => ({
    company_name: profile?.company_name || "",
    what_they_do: profile?.what_they_do || "",
  }));

  const confidenceColor = useMemo(() => ({
    high:   "bg-emerald-100 text-emerald-900 border-emerald-800",
    medium: "bg-amber-100 text-amber-900 border-amber-800",
    low:    "bg-red-100 text-red-900 border-red-800",
  }[profile?.confidence] || "bg-neutral-100 text-neutral-900 border-neutral-700"), [profile]);

  return (
    <div className="border-t-2 border-[var(--peak-ink)] bg-[var(--peak-bg)] max-h-[50%] overflow-y-auto" data-testid="copilot-verify-panel">
      <div className="px-4 py-3 border-b border-[var(--peak-border)] bg-white flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--peak-muted)]">Business Profile</div>
          <div className="font-heading font-black tracking-tight text-base truncate">
            {profile?.company_name || "Untitled"}
          </div>
        </div>
        <span
          className={`text-[9px] font-mono uppercase px-1.5 py-0.5 border ${confidenceColor}`}
          data-testid="copilot-confidence"
        >
          {profile?.confidence || "medium"} conf.
        </span>
      </div>

      <div className="p-4 space-y-3 text-sm">
        {editing ? (
          <>
            <label className="block">
              <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] mb-1">Company name</div>
              <Input
                value={edits.company_name}
                onChange={(e) => setEdits((v) => ({ ...v, company_name: e.target.value }))}
                data-testid="copilot-edit-company"
                className="h-9 border-2 border-[var(--peak-ink)]"
              />
            </label>
            <label className="block">
              <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] mb-1">What they do</div>
              <Textarea
                value={edits.what_they_do}
                onChange={(e) => setEdits((v) => ({ ...v, what_they_do: e.target.value }))}
                rows={3}
                data-testid="copilot-edit-desc"
                className="border-2 border-[var(--peak-ink)] text-xs"
              />
            </label>
          </>
        ) : (
          <>
            {profile?.what_they_do && (
              <p className="text-sm text-[var(--peak-ink)]/80 leading-relaxed" data-testid="copilot-profile-desc">
                {profile.what_they_do}
              </p>
            )}
            {profile?.products_or_services?.length > 0 && (
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] mb-1.5">
                  Products / Services ({profile.products_or_services.length})
                </div>
                <ul className="space-y-1.5">
                  {profile.products_or_services.map((p, i) => (
                    <li key={i} className="border-2 border-[var(--peak-ink)] bg-white p-2" data-testid={`copilot-product-${i}`}>
                      <div className="font-semibold text-xs">{p.name}</div>
                      {p.description && (
                        <div className="text-[11px] text-[var(--peak-muted)] mt-0.5">{p.description}</div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {profile?.target_customers?.length > 0 && (
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] mb-1.5">
                  Target Customers
                </div>
                <div className="flex flex-wrap gap-1">
                  {profile.target_customers.map((t, i) => (
                    <span key={i} className="text-[11px] font-mono bg-white border border-[var(--peak-ink)] px-1.5 py-0.5">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="p-3 flex items-center gap-2 border-t border-[var(--peak-border)] bg-white">
        <Button
          onClick={() => onVerify(true, editing ? edits : null)}
          disabled={busy}
          data-testid="copilot-verify-approve"
          className="flex-1 h-9 bg-[var(--peak-accent)] hover:bg-[var(--peak-ink)] text-white font-heading font-bold uppercase text-xs tracking-wide"
        >
          <CheckCircle2 className="w-4 h-4 mr-1.5" />
          {editing ? "Save & Continue" : "Looks Right"}
        </Button>
        <Button
          onClick={() => setEditing((e) => !e)}
          disabled={busy}
          data-testid="copilot-verify-edit"
          variant="outline"
          className="h-9 border-2 border-[var(--peak-ink)] font-heading font-bold uppercase text-xs tracking-wide"
        >
          <Edit3 className="w-3.5 h-3.5 mr-1.5" />
          {editing ? "Cancel" : "Edit"}
        </Button>
        <Button
          onClick={() => onVerify(false)}
          disabled={busy}
          data-testid="copilot-verify-reject"
          variant="outline"
          className="h-9 border-2 border-[var(--peak-ink)] font-heading font-bold uppercase text-xs tracking-wide"
        >
          Redo
        </Button>
      </div>
    </div>
  );
}

/** Small launcher button — mounted globally so users can always reopen. */
export function CopilotLauncher() {
  const { open, openCopilot, session } = useCopilot();
  if (open) return null;
  const hasUnread = session?.state && session.state !== "completed" && session.state !== "ready_for_wave2";
  return (
    <button
      onClick={() => openCopilot()}
      data-testid="copilot-launcher"
      title="Open Peak Copilot"
      className="fixed bottom-6 right-6 z-40 bg-[var(--peak-accent)] hover:bg-[var(--peak-ink)] text-white w-14 h-14 border-2 border-[var(--peak-ink)] shadow-[4px_4px_0_0_rgba(0,0,0,0.9)] hover:shadow-[6px_6px_0_0_rgba(0,0,0,0.9)] transition-all flex items-center justify-center"
    >
      <Sparkles className="w-6 h-6" strokeWidth={2.5} />
      {hasUnread && (
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-white border-2 border-[var(--peak-ink)] animate-pulse" />
      )}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// Wave 2 walkthrough panel
// ─────────────────────────────────────────────────────────────

const KIND_META = {
  pipeline:   { label: "Pipeline",   Icon: GitBranch, tint: "bg-[var(--peak-blue)] text-white border-[var(--peak-blue)]" },
  funnel:     { label: "Funnel",     Icon: Workflow,  tint: "bg-[var(--peak-accent)] text-white border-[var(--peak-accent)]" },
  automation: { label: "Automation", Icon: Zap,       tint: "bg-[var(--peak-ink)] text-white border-[var(--peak-ink)]" },
};

function WalkthroughPanel({
  recommendations, walkthroughIndex, state, busy,
  onApprove, onSkip, onExplain, onEdit, onFinish,
}) {
  const { narrateStart, narrateNext, narrateStop, session } = useCopilot();
  const narratedId = session?.narrated_rec_id || null;
  const walkthroughActive = !!narratedId;
  const total = recommendations.length;
  const approvedCount = recommendations.filter((r) => r.status === "approved").length;
  const skippedCount = recommendations.filter((r) => r.status === "skipped").length;
  const current = state === "completed" ? null : recommendations[walkthroughIndex];
  const isDone = state === "completed" || walkthroughIndex >= total;

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: "", why: "" });

  useEffect(() => {
    if (!current) return;
    if (editingId === current.id) return;
    setEditingId(null);
  }, [current, editingId]);

  if (isDone) {
    return (
      <div className="border-t-2 border-[var(--peak-ink)] bg-white p-5 flex flex-col items-center text-center gap-3" data-testid="copilot-walkthrough-complete">
        <div className="w-12 h-12 bg-[var(--peak-accent)] text-white flex items-center justify-center border-2 border-[var(--peak-ink)]">
          <PartyPopper className="w-6 h-6" strokeWidth={2.5} />
        </div>
        <div className="font-heading font-black text-lg tracking-tight">All wrapped up</div>
        <div className="text-sm text-[var(--peak-muted)]">
          Approved <b>{approvedCount}</b> · Skipped <b>{skippedCount}</b> · Total <b>{total}</b>
        </div>
        <ul className="w-full text-left mt-2 space-y-1 max-h-56 overflow-y-auto">
          {recommendations.map((r) => (
            <li key={r.id} className="flex items-center gap-2 text-xs" data-testid={`copilot-summary-rec-${r.id}`}>
              <span className={`inline-block w-2 h-2 flex-none ${r.status === "approved" ? "bg-emerald-500" : "bg-neutral-400"}`} />
              <span className="font-mono uppercase text-[10px] tracking-wider text-[var(--peak-muted)]">{r.kind}</span>
              <span className="truncate">{r.name}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (!current) return null;

  const meta = KIND_META[current.kind] || KIND_META.pipeline;
  const Icon = meta.Icon;

  const startEdit = () => {
    setEditingId(current.id);
    setEditDraft({ name: current.name, why: current.why });
  };
  const saveEdit = async () => {
    await onEdit(current.id, { name: editDraft.name, why: editDraft.why });
    setEditingId(null);
  };

  return (
    <div className="border-t-2 border-[var(--peak-ink)] bg-[var(--peak-bg)] max-h-[60%] overflow-y-auto" data-testid="copilot-walkthrough-panel">
      {/* Progress strip */}
      <div className="px-4 pt-3 pb-2 border-b border-[var(--peak-border)] bg-white flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--peak-muted)]" data-testid="copilot-progress">
          Recommendation {Math.min(walkthroughIndex + 1, total)} of {total}
        </div>
        <div className="flex items-center gap-2">
          {walkthroughActive ? (
            <>
              <button
                onClick={narrateNext}
                disabled={busy}
                data-testid="copilot-drawer-narrate-next"
                className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-accent)] hover:text-[var(--peak-ink)]"
              >
                Next →
              </button>
              <button
                onClick={narrateStop}
                disabled={busy}
                data-testid="copilot-drawer-narrate-stop"
                className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] hover:text-[var(--peak-ink)]"
              >
                End walkthrough
              </button>
            </>
          ) : (
            <button
              onClick={narrateStart}
              disabled={busy}
              data-testid="copilot-drawer-narrate-start"
              className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-blue)] hover:text-[var(--peak-ink)]"
            >
              Walk me through →
            </button>
          )}
          <button
            onClick={onFinish}
            disabled={busy}
            data-testid="copilot-finish-walkthrough"
            className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] hover:text-[var(--peak-ink)]"
          >
            Finish
          </button>
        </div>
      </div>
      {/* Progress bar */}
      <div className="h-1 bg-[var(--peak-border)] w-full">
        <div
          className="h-full bg-[var(--peak-accent)] transition-all"
          style={{ width: `${total ? ((approvedCount + skippedCount) / total) * 100 : 0}%` }}
        />
      </div>

      {/* Recommendation card */}
      <div className="p-4 space-y-3" data-testid={`copilot-rec-card-${current.id}`}>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider border-2 ${meta.tint}`}>
            <Icon className="w-3 h-3" strokeWidth={2.5} />
            {meta.label}
          </span>
          <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--peak-muted)] truncate">
            for {current.product_name}
          </span>
        </div>

        {editingId === current.id ? (
          <>
            <Input
              value={editDraft.name}
              onChange={(e) => setEditDraft((v) => ({ ...v, name: e.target.value }))}
              data-testid="copilot-rec-edit-name"
              className="h-9 border-2 border-[var(--peak-ink)] font-heading font-bold text-base"
            />
            <Textarea
              value={editDraft.why}
              onChange={(e) => setEditDraft((v) => ({ ...v, why: e.target.value }))}
              rows={3}
              data-testid="copilot-rec-edit-why"
              className="border-2 border-[var(--peak-ink)] text-xs"
            />
          </>
        ) : (
          <>
            <div className="font-heading font-black text-lg tracking-tight leading-tight" data-testid="copilot-rec-name">
              {current.name}
            </div>
            {current.why && (
              <p className="text-sm text-[var(--peak-ink)]/80 leading-relaxed" data-testid="copilot-rec-why">
                {current.why}
              </p>
            )}
          </>
        )}

        <RecConfigPreview rec={current} />
      </div>

      {/* Actions */}
      <div className="p-3 border-t border-[var(--peak-border)] bg-white flex flex-wrap items-center gap-2">
        {editingId === current.id ? (
          <>
            <Button
              onClick={saveEdit}
              disabled={busy}
              data-testid="copilot-rec-save"
              className="flex-1 h-9 bg-[var(--peak-ink)] hover:bg-[var(--peak-accent)] text-white font-heading font-bold uppercase text-xs tracking-wide"
            >
              Save changes
            </Button>
            <Button
              onClick={() => setEditingId(null)}
              disabled={busy}
              data-testid="copilot-rec-cancel-edit"
              variant="outline"
              className="h-9 border-2 border-[var(--peak-ink)] font-heading font-bold uppercase text-xs tracking-wide"
            >
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={() => onApprove(current.id)}
              disabled={busy}
              data-testid="copilot-rec-approve"
              className="flex-1 h-9 bg-[var(--peak-accent)] hover:bg-[var(--peak-ink)] text-white font-heading font-bold uppercase text-xs tracking-wide"
            >
              <CheckCircle2 className="w-4 h-4 mr-1.5" />
              Approve & Create
            </Button>
            <Button
              onClick={() => onSkip(current.id)}
              disabled={busy}
              data-testid="copilot-rec-skip"
              variant="outline"
              className="h-9 border-2 border-[var(--peak-ink)] font-heading font-bold uppercase text-xs tracking-wide"
              title="Skip this recommendation"
            >
              <SkipForward className="w-3.5 h-3.5 mr-1" />
              Skip
            </Button>
            <div className="w-full flex items-center gap-2">
              <button
                onClick={() => onExplain(current.id)}
                disabled={busy}
                data-testid="copilot-rec-explain"
                className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] hover:text-[var(--peak-ink)] flex items-center gap-1"
              >
                <HelpCircle className="w-3 h-3" /> Explain more
              </button>
              <button
                onClick={startEdit}
                disabled={busy}
                data-testid="copilot-rec-edit"
                className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] hover:text-[var(--peak-ink)] flex items-center gap-1"
              >
                <Edit3 className="w-3 h-3" /> Edit
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function RecConfigPreview({ rec }) {
  const cfg = rec.config || {};
  if (rec.kind === "pipeline") {
    const stages = cfg.stages || [];
    return (
      <div data-testid="copilot-rec-config-pipeline">
        <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] mb-1.5">
          {stages.length} stages
        </div>
        <ol className="border-2 border-[var(--peak-ink)] bg-white divide-y divide-[var(--peak-border)]">
          {stages.map((s, i) => (
            <li key={i} className="px-2 py-1.5 flex items-center gap-2 text-xs">
              <span className="w-5 h-5 flex-none bg-[var(--peak-ink)] text-white flex items-center justify-center font-mono text-[10px]">
                {i + 1}
              </span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      </div>
    );
  }
  if (rec.kind === "funnel") {
    return (
      <div data-testid="copilot-rec-config-funnel">
        <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] mb-1.5">
          Landing page preview
        </div>
        <div className="border-2 border-[var(--peak-ink)] bg-white p-3 space-y-2">
          <div className="font-heading font-black text-base leading-tight tracking-tight">
            {cfg.headline || "Headline"}
          </div>
          {cfg.subheadline && (
            <div className="text-xs text-[var(--peak-muted)]">{cfg.subheadline}</div>
          )}
          {cfg.body_text && (
            <div className="text-[11px] text-[var(--peak-ink)]/70 line-clamp-3">{cfg.body_text}</div>
          )}
          <div className="pt-2 mt-2 border-t border-[var(--peak-border)] flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-[var(--peak-muted)]">
            <span>{cfg.form_title || "Contact form"}</span>
            <span className="px-2 py-0.5 bg-[var(--peak-accent)] text-white">{cfg.form_cta || "Send"}</span>
          </div>
        </div>
      </div>
    );
  }
  if (rec.kind === "automation") {
    const actions = cfg.actions || [];
    return (
      <div data-testid="copilot-rec-config-automation">
        <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[var(--peak-muted)] mb-1.5">
          Trigger + {actions.length} action{actions.length === 1 ? "" : "s"}
        </div>
        <div className="border-2 border-[var(--peak-ink)] bg-white divide-y divide-[var(--peak-border)]">
          <div className="px-2 py-1.5 flex items-center gap-2 text-xs bg-[var(--peak-bg)]">
            <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--peak-accent)]">Trigger</span>
            <span className="font-mono">{cfg.trigger_type || "contact_created"}</span>
          </div>
          {actions.map((a, i) => (
            <div key={i} className="px-2 py-1.5 flex items-center gap-2 text-xs">
              <span className="w-5 h-5 flex-none bg-[var(--peak-ink)] text-white flex items-center justify-center font-mono text-[10px]">
                {i + 1}
              </span>
              <span className="font-mono uppercase text-[10px] tracking-wider text-[var(--peak-muted)]">
                {a.type}
              </span>
              {a.wait_before_minutes ? (
                <span className="text-[10px] font-mono text-[var(--peak-muted)]">wait {a.wait_before_minutes}m</span>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
}
